import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import CustomToolTipICon from "../components/tooltips/CustomToolTipICon"; // Import the tooltip component
import CustomAlert from "../components/alert/CustomAlert"; // Import CustomAlert
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoaderComponent from "../components/loaderCompent/LoaderComponent"; // Import the loader component
import Modal from "../components/Modal"; // Import a modal component if available
const apiUrl = process.env.REACT_APP_API_URL;

const AdditionalInfo = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false); // Add loading state
    const [showModal, setShowModal] = useState(false); // State to control modal visibility
    const [formData, setFormData] = useState({
        companyName: "",
        userName: "",
        userEmail: "",
        companyPAN: "",
        companyWebsite: "",
        companyGST: "",
        gstNumber: "", // Added this field
        letterHead: null,
        proofOfIdentity: null,
    });
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState(null); // State for CustomAlert

    useEffect(() => {
        const fetchOrganisationDetails = async () => {
            setLoading(true);
            try {
                const user = localStorage.getItem("user");
                const token = JSON.parse(user)?.result?.token;
                if (token) {
                    const decodedToken = jwtDecode(token);
                    const organisationId = decodedToken?.organisationId;

                    if (!organisationId) {
                        setAlert({
                            message: "Organisation ID not found in token.",
                            type: "error",
                        });
                        setLoading(false);
                        return;
                    }

                    // Fetch organisation details
                    const response = await fetch(
                        `${apiUrl}/organisation/${organisationId}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    const data = await response.json();
                    if (data.success) {
                        const organisation = data.result;

                        // Check if additional info is already added
                        if (organisation.additionalInfoIsAdded) {
                            setShowModal(true); // Show modal if additional info is already added
                        }

                        // Pre-fill form data with organisation details
                        setFormData((prevData) => ({
                            ...prevData,
                            companyName: organisation.name || "",
                            userName: organisation.buyerId.username || "",
                            userEmail: organisation.buyerId.email || "",
                        }));
                    } else {
                        setAlert({
                            message:
                                data.error?.message ||
                                "Failed to fetch organisation details.",
                            type: "error",
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching organisation details:", error);
                setAlert({
                    message:
                        "An error occurred while fetching organisation details.",
                    type: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOrganisationDetails();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (files) {
            const file = files[0];
            const mimeTypes = {
                pdf: "application/pdf",
                doc: "application/msword",
                docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                jpg: "image/jpeg",
                jpeg: "image/jpeg",
                png: "image/png",
            };

            // Validate file MIME type
            if (!Object.values(mimeTypes).includes(file.type)) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: `Only Images, Documents and Pdf files Allowed.`,
                }));
                return; // Stop further processing
            } else {
                // Clear error if file type is valid
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: null,
                }));
            }
        }

        setFormData({
            ...formData,
            [name]: files ? files[0] : value,
        });
    };

    const validateStep = () => {
        let newErrors = {};
        if (currentStep === 1) {
            if (!formData.companyPAN.trim())
                newErrors.companyPAN = "Company PAN is required";
            if (!formData.companyWebsite.trim())
                newErrors.companyWebsite = "Company Website is required";
        } else if (currentStep === 2) {
            if (!formData.letterHead)
                newErrors.letterHead = "Upload Letter Head is required";
        } else if (currentStep === 3) {
            if (!formData.proofOfIdentity)
                newErrors.proofOfIdentity = "Proof of Identity is required";
            if (!formData.gstNumber.trim() && !formData.companyGST)
                newErrors.gstNumber = "Either GST Number or GST Document is required"; // Updated validation
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep()) {
            setCurrentStep((prevStep) => prevStep + 1);
        }
    };

    const handleBack = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("storage", "local");
        formData.append("type", "image");
        formData.append("objectType", "image");

        const response = await fetch(`${apiUrl}/media/`, {
            method: "POST",
            body: formData,
        });

        const data = await response.json();
        if (data.success) {
            return data.result.url;
        } else {
            throw new Error(data.error.message || "File upload failed");
        }
    };

    const handleSubmit = async () => {
        if (validateStep()) {
            setLoading(true); // Show loader
            try {
                const user = localStorage.getItem("user");
                const token = JSON.parse(user)?.result?.token;
                const decodedToken = jwtDecode(token);
                const organisationId = decodedToken?.organisationId;
                const userId = decodedToken?._id;

                if (!organisationId) {
                    setAlert({
                        message: "Organisation ID not found in token.",
                        type: "error",
                    });
                    setLoading(false); // Hide loader
                    return;
                }

                // Upload files and get their URLs
                const authorisationDocumentUrl = formData.letterHead
                    ? await uploadFile(formData.letterHead)
                    : null;
                const gstCertificateUrl = formData.companyGST
                    ? await uploadFile(formData.companyGST)
                    : null;

                const proofOfIdentityUrl = formData.proofOfIdentity
                    ? await uploadFile(formData.proofOfIdentity)
                    : null;

                // Prepare the payload for the PUT request
                const payload = {
                    authorisation_document: authorisationDocumentUrl,
                    GST_Certificate: gstCertificateUrl,
                    PAN: formData.companyPAN,
                    companyWebsite: formData.companyWebsite,
                    gstNumber: formData.gstNumber, // Added GST Number
                    additionalInfoIsAdded: true,
                    proofOfIdentity: proofOfIdentityUrl,
                };
                // Make the PUT API call to update the organisation
                const updateResponse = await fetch(
                    `${apiUrl}/organisation/${organisationId}`, // Updated API URL
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(payload),
                    }
                );

                const updateData = await updateResponse.json();
                if (updateResponse.ok) {
                    // Update the user with proofOfIdentity URL
                    if (proofOfIdentityUrl) {
                        const userUpdatePayload = {
                            proofOfIdentity: proofOfIdentityUrl,
                        };

                        const userUpdateResponse = await fetch(
                            `${apiUrl}/user/${userId}`, // Updated API URL
                            {
                                method: "PUT",
                                headers: {
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(userUpdatePayload),
                            }
                        );

                        const userUpdateData = await userUpdateResponse.json();
                        if (userUpdateResponse.ok) {
                            // Send email with updated parameters
                            const emailPayload = {
                                email: formData.userEmail,
                                userId: userId,
                                organisationName: formData.companyName,
                                companyWebsite: formData.companyWebsite,
                                organisationPAN: formData.companyPAN,
                                organisationGST: gstCertificateUrl,
                                gstNumber: formData.gstNumber, // Added GST Number
                                contactPerson: formData.userName,
                                authorisationDocumentUrl:
                                    authorisationDocumentUrl,
                                proofOfIdentityUrl: proofOfIdentityUrl,
                            };

                            try {
                                const emailResponse = await fetch(
                                    `${apiUrl}/user/sendOrganisationCredentialEmail`, // Updated API URL
                                    {
                                        method: "POST",
                                        headers: {
                                            "Content-Type": "application/json",
                                        },
                                        body: JSON.stringify(emailPayload),
                                    }
                                );

                                const emailData = await emailResponse.json();
                                if (emailResponse.ok) {
                                    setAlert({
                                        message: "Additional info added successfully!",
                                        type: "success",
                                    });
                                    setTimeout(() => {
                                        navigate("/"); 
                                    }, 2000);
                                    
                                } else {
                                    console.error(
                                        "Failed to send email:",
                                        emailData.error?.message
                                    );
                                    setAlert({
                                        message: "Failed to send email.",
                                        type: "error",
                                    });
                                }
                            } catch (emailError) {
                                console.error(
                                    "Error sending email:",
                                    emailError
                                );
                                setAlert({
                                    message: "An error occurred while sending the email.",
                                    type: "error",
                                });
                            }
                        } else {
                            throw new Error(
                                userUpdateData.error?.message ||
                                    "Failed to update user"
                            );
                        }
                    }
                } else {
                    throw new Error(
                        updateData.error?.message ||
                            "Failed to update organisation"
                    );
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                setAlert({
                    message: "An error occurred while submitting the form.",
                    type: "error",
                });
            } finally {
                setLoading(false); // Hide loader
            }
        }
    };

    return (
        <div className="bg-white min-h-screen flex items-center justify-center p-4 relative">
            {loading && ( // Show loader if loading is true
                <div className="flex flex-col items-center justify-center min-h-screen bg-transparent absolute inset-0  z-50">
                    <div className="min-h-12 min-w-12 bg-white rounded-lg shadow-lg p-6 flex flex-col items-center justify-center">
                        <LoaderComponent width="80" height="80" />
                        <p className="text-gray-700 text-lg mt-4">Loading...</p>
                    </div>
                </div>
            )}

            {/* Modal for additional info already added */}
            {/* {showModal && (
                <Modal
                    title="Information Already Submitted"
                    onClose={() => navigate("/")} // Redirect to home on modal close
                >
                    <p>
                        You have already filled in the additional information.
                    </p>
                </Modal>
            )} */}

            {/* Existing form content */}

            <div
                className={`relative min-w-[600px] mx-auto mt-4 p-6 bg-green-100 shadow-lg rounded-lg ${
                    loading ? "opacity-50 pointer-events-none" : ""
                }`}
            >
                {/* Close Button */}
                <div
                    className="w-full flex justify-end"
                    onClick={() => navigate("/")}
                >
                    <CustomToolTipICon
                        icon={faTimes}
                        color="gray"
                        text="Close"
                        size="lg"
                        disable={false}
                    />
                </div>

                <h2 className="text-center text-3xl font-bold text-[#067C4E] mb-6">
                    Additional Information
                </h2>

                <div className="flex justify-between items-center mb-6">
                    <div
                        className={`flex-1 text-center ${
                            currentStep === 1
                                ? "text-[#067C4E]"
                                : "text-gray-400"
                        }`}
                    >
                        <div
                            className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                                currentStep === 1
                                    ? "bg-[#067C4E] text-white"
                                    : "bg-gray-300 text-gray-600"
                            }`}
                        >
                            1
                        </div>
                        <span className="text-sm font-medium">
                            Company Info
                        </span>
                    </div>
                    <div className="flex-1 border-t-2 border-gray-300"></div>
                    <div
                        className={`flex-1 text-center ${
                            currentStep === 2
                                ? "text-[#067C4E]"
                                : "text-gray-400"
                        }`}
                    >
                        <div
                            className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                                currentStep === 2
                                    ? "bg-[#067C4E] text-white"
                                    : "bg-gray-300 text-gray-600"
                            }`}
                        >
                            2
                        </div>
                        <span className="text-sm font-medium">
                            Authorization Info
                        </span>
                    </div>
                    <div className="flex-1 border-t-2 border-gray-300"></div>
                    <div
                        className={`flex-1 text-center ${
                            currentStep === 3
                                ? "text-[#067C4E]"
                                : "text-gray-400"
                        }`}
                    >
                        <div
                            className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                                currentStep === 3
                                    ? "bg-[#067C4E] text-white"
                                    : "bg-gray-300 text-gray-600"
                            }`}
                        >
                            3
                        </div>
                        <span className="text-sm font-medium">
                            Additional Info
                        </span>
                    </div>
                </div>

                {/* Add a fixed height container for step content */}
                <div className="min-h-[400px]">
                    {" "}
                    {/* Adjust the height as needed */}
                    {currentStep === 1 && (
                        <div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Company Name
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    disabled
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    User Name
                                </label>
                                <input
                                    type="text"
                                    name="userName"
                                    value={formData.userName}
                                    disabled
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    User Email
                                </label>
                                <input
                                    type="email"
                                    name="userEmail"
                                    value={formData.userEmail}
                                    disabled
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Company PAN
                                </label>
                                <input
                                    type="text"
                                    name="companyPAN"
                                    value={formData.companyPAN}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                                {errors.companyPAN && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.companyPAN}
                                    </p>
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Company Website
                                </label>
                                <input
                                    type="text"
                                    name="companyWebsite"
                                    value={formData.companyWebsite}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                                {errors.companyWebsite && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.companyWebsite}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    {currentStep === 2 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                Authorization of Signatory
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Download the sample format, edit it, and upload
                                the signed document.
                            </p>
                            <a
                                href="/Format_AUTHORITY_LETTER_AMPEX-final.doc"
                                download
                                className="text-blue-500 underline mb-4 inline-block"
                            >
                                Download Sample Format
                            </a>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Upload Letter Head
                                </label>
                                <input
                                    type="file"
                                    name="letterHead"
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                                <div className="text-xs rounded-lg text-gray-600 bg-green-200 p-2 mt-1 flex items-center">
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className="text-blue-500 mr-2"
                                    />
                                    Note: Only Images, Documents and Pdf files
                                    Allowed.
                                </div>
                                {errors.letterHead && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.letterHead}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    {currentStep === 3 && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                Additional Information
                            </h3>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    GST Number
                                </label>
                                <input
                                    type="text"
                                    name="gstNumber"
                                    value={formData.gstNumber}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                                {errors.gstNumber && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.gstNumber}
                                    </p>
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Upload GST Certificate
                                </label>
                                <input
                                    type="file"
                                    name="companyGST"
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                                <div className="text-xs rounded-lg text-gray-600 bg-green-200 p-2 mt-1 flex items-center">
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className="text-blue-500 mr-2"
                                    />
                                    Note: Only Images, Documents and Pdf files
                                    Allowed.
                                </div>
                                {errors.companyGST && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.companyGST}
                                    </p>
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Proof of Identity of Authorized Signatory
                                </label>
                                <input
                                    type="file"
                                    name="proofOfIdentity"
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#067C4E]"
                                />
                                <div className="text-xs rounded-lg text-gray-600 bg-green-200 p-2 mt-1 flex items-center">
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className="text-blue-500 mr-2"
                                    />
                                    Note: Only Images, Documents and Pdf files
                                    Allowed.
                                </div>
                                {errors.proofOfIdentity && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.proofOfIdentity}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-between mt-6">
                    {currentStep > 1 && (
                        <button
                            type="button"
                            onClick={handleBack}
                            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                        >
                            Back
                        </button>
                    )}
                    {currentStep < 3 ? (
                        <button
                            type="button"
                            onClick={handleNext}
                            className="px-6 py-2 bg-[#067C4E] text-white rounded-lg hover:bg-[#045C3A]"
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="px-6 py-2 bg-[#067C4E] text-white rounded-lg hover:bg-[#045C3A]"
                        >
                            Submit
                        </button>
                    )}
                </div>
            </div>
            {alert && (
                <CustomAlert
                    message={alert.message}
                    type={alert.type}
                    onClose={() => setAlert(null)}
                />
            )}
        </div>
    );
};

export default AdditionalInfo;
